import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { useReportView } from './useReportView';
import { ReportChart } from './ReportChart';
import { ReportPivot } from './ReportPivot';
import './reportGrid.css';

// ════════════════════════════════════════════════════════════════════════════
// ReportGrid — sabit raporlar için ÖZELLEŞTİRİLEBİLİR grid (TanStack Table motoru).
//
// Yeni grid kütüphanesi EKLENMEDİ; DevExpress/AG-Grid-Enterprise'ın "pahalı" özelliklerinin
// TanStack'te ücretsiz olanları açıldı:
//   • Kolon sürükle-sıra + göster/gizle + genişlik + KALICI görünüm
//   • Satır GRUPLAMA + grup alt toplamları (katlanır)
//   • ÖZET/TOPLAM satırı (filtreye duyarlı)
//   • Hızlı arama (tüm kolonlar) + ÇOKLU sıralama (shift) + yoğunluk
//   • Sağa kolon DONDURMA + koşullu renklendirme
//
// Kolon sözleşmesi:
//   { key, label, num?, pct?, bold?, sticky?, fmt?, cell?, minWidth?, filter?,
//     agg?, tone? }
//     agg  → grup/özet toplama: 'sum' | 'avg' | 'none' (varsayılan num→sum, pct→avg)
//     tone → koşullu renk: (value) => cssColor | null (hücre metni boyanır)
// ════════════════════════════════════════════════════════════════════════════

const NUM_OPS = [
  { value: 'ge', label: '≥' },
  { value: 'le', label: '≤' },
  { value: 'eq', label: '=' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
];
const PAGE_SIZES = [25, 50, 100, 250];

const filterTypeOf = (col) => (col.filter === false ? false : (col.filter || (col.num || col.pct ? 'number' : 'text')));
const aggOf = (col) => col.agg || (col.pct ? 'avg' : col.num ? 'sum' : 'none');
const isNumericCol = (col) => !!(col.num || col.pct);

function textFilter(row, columnId, value) {
  const cell = row.getValue(columnId);
  return String(cell ?? '').toLocaleLowerCase('tr').includes(String(value).toLocaleLowerCase('tr'));
}
function numberFilter(row, columnId, value) {
  if (!value || value.val === '' || value.val == null) return true;
  const cell = Number(row.getValue(columnId)) || 0;
  const target = Number(value.val);
  if (Number.isNaN(target)) return true;
  switch (value.op) {
    case 'le': return cell <= target;
    case 'eq': return cell === target;
    case 'gt': return cell > target;
    case 'lt': return cell < target;
    case 'ge':
    default: return cell >= target;
  }
}
// Değer listesi filtresi (kolon başlığındaki huni → işaretlenen değerler).
// value.values RAW hücre değerlerinin string hâlidir; boş hücre '' anahtarıyla tutulur.
function valuesFilter(row, columnId, value) {
  const list = value?.values;
  if (!Array.isArray(list) || list.length === 0) return true;   // hiçbiri seçili değil = filtre yok
  return list.includes(String(row.getValue(columnId) ?? ''));
}
// Tek dağıtıcı: filtre değerinin ŞEKLİNE bakar. Kayıtlı görünümler (localStorage) eski
// biçimleri taşıyabilir — düz string "içeren metin", {op,val} sayısal karşılaştırma —
// bu yüzden ikisi de desteklenmeye devam eder, yoksa eski görünümler bozulurdu.
function columnFilter(row, columnId, value) {
  if (value == null || value === '') return true;
  if (typeof value === 'string') return textFilter(row, columnId, value);
  if (Array.isArray(value?.values)) return valuesFilter(row, columnId, value);
  return numberFilter(row, columnId, value);
}

// Hızlı arama: satırın HERHANGİ bir kolonunda geçiyor mu (tr-duyarsız).
function globalFilterFn(row, _columnId, value) {
  const q = String(value ?? '').toLocaleLowerCase('tr').trim();
  if (!q) return true;
  return Object.values(row.original).some((v) => String(v ?? '').toLocaleLowerCase('tr').includes(q));
}

export function ReportGrid({ reportKey, columns, rows, idColumn = true, onVisibleRowsChange }) {
  const columnKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const colByKey = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c])), [columns]);
  const { view, patch, reset, isCustomized, savedViews, saveCurrentView, applyView, deleteView } = useReportView(reportKey, columnKeys);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);

  // Sağ kolon paneli — VARSAYILAN AÇIK (AG Grid sideBar gibi). Tercih rapor bazlı hatırlanır.
  const [panelOpen, setPanelOpen] = useState(() => {
    try { return localStorage.getItem(`reportPanel.${reportKey}`) !== '0'; } catch { return true; }
  });
  const togglePanel = useCallback(() => {
    setPanelOpen((o) => {
      const next = !o;
      try { localStorage.setItem(`reportPanel.${reportKey}`, next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  }, [reportKey]);

  const [openFilter, setOpenFilter] = useState(null); // { key, rect }
  const [draft, setDraft] = useState('');
  const [filterSearch, setFilterSearch] = useState(''); // filtre listesindeki arama kutusu

  const [expanded, setExpanded] = useState(true); // gruplar varsayılan açık
  const [pageIndex, setPageIndex] = useState(0);
  const [mode, setMode] = useState('table'); // 'table' | 'chart' | 'pivot'
  // Hücre aralığı seçimi (Excel gibi sürükle-seç + Ctrl+C). {r1,c1,r2,c2} görünür indekslerle.
  const [sel, setSel] = useState(null);
  const selecting = useRef(false);
  const [copied, setCopied] = useState(false);
  const dragKey = useRef(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const panelDragKey = useRef(null);
  const [panelDragOverKey, setPanelDragOverKey] = useState(null);
  const scrollRef = useRef(null);
  const grouping = view.grouping;
  const isGrouped = grouping.length > 0;

  // ── TanStack kolon tanımları ──
  const tableColumns = useMemo(() => columns.map((col) => ({
    id: col.key,
    accessorKey: col.key,
    header: col.label,
    enableSorting: true,
    enableHiding: !col.sticky,
    enableGrouping: !isNumericCol(col) && !col.sticky, // sadece metin kolonlarıyla grupla
    filterFn: columnFilter,
    aggregationFn: aggOf(col) === 'avg' ? 'mean' : 'sum',
    size: view.widths[col.key] || col.minWidth || (col.sticky ? 200 : 120),
    minSize: 64,
    meta: col,
  })), [columns, view.widths]);

  const columnFilters = useMemo(
    () => Object.entries(view.filters).map(([id, value]) => ({ id, value })),
    [view.filters],
  );
  const columnVisibility = useMemo(
    () => Object.fromEntries(view.hidden.map((k) => [k, false])),
    [view.hidden],
  );
  const stickyKey = useMemo(() => columns.find((c) => c.sticky)?.key, [columns]);
  const columnPinning = useMemo(
    () => ({ left: stickyKey ? [stickyKey] : [], right: view.pinnedRight }),
    [stickyKey, view.pinnedRight],
  );

  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    state: {
      sorting: view.sorting,
      columnFilters,
      globalFilter: view.globalFilter,
      columnVisibility,
      columnOrder: view.order,
      columnPinning,
      grouping,
      expanded,
      pagination: { pageIndex: 0, pageSize: isGrouped ? Math.max(rows.length, 1) : view.pageSize },
    },
    onSortingChange: (updater) => {
      const nextSort = typeof updater === 'function' ? updater(view.sorting) : updater;
      patch({ sorting: nextSort });
    },
    onExpandedChange: setExpanded,
    globalFilterFn,
    enableMultiSort: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    columnResizeMode: 'onEnd',
  });

  // Filtreli (gruplanmamış) satırlar → Excel/PDF + özet toplam satırı.
  const filteredOriginals = useMemo(
    () => table.getFilteredRowModel().rows.filter((r) => !r.getIsGrouped()).map((r) => r.original),
    [table, view.filters, view.globalFilter, rows], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filteredCount = filteredOriginals.length;
  useEffect(() => { onVisibleRowsChange?.(filteredOriginals); }, [filteredOriginals, onVisibleRowsChange]);

  const pageCount = isGrouped ? 1 : (Math.ceil(filteredCount / view.pageSize) || 1);
  const safePage = Math.min(pageIndex, pageCount - 1);

  // Render satırları: gruplu → tüm ağaç; düz → sayfalı.
  const modelRows = table.getRowModel().rows;
  const renderRows = useMemo(() => {
    if (isGrouped) return modelRows;
    const start = safePage * view.pageSize;
    return modelRows.slice(start, start + view.pageSize);
  }, [isGrouped, modelRows, safePage, view.pageSize]);

  // ── Hücre aralığı seçimi + kopyalama (Excel gibi) ──
  const visibleLeafCols = table.getVisibleLeafColumns();
  const rangeEnabled = mode === 'table' && !isGrouped;
  const selNorm = useMemo(
    () => (sel
      ? { r1: Math.min(sel.r1, sel.r2), r2: Math.max(sel.r1, sel.r2), c1: Math.min(sel.c1, sel.c2), c2: Math.max(sel.c1, sel.c2) }
      : null),
    [sel],
  );
  const inSel = (r, c) => selNorm && r >= selNorm.r1 && r <= selNorm.r2 && c >= selNorm.c1 && c <= selNorm.c2;

  useEffect(() => {
    const up = () => { selecting.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  useEffect(() => {
    if (!rangeEnabled || !selNorm) return undefined;
    const onKey = (e) => {
      if (!((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C'))) return;
      // Metin girişindeyken kopyalamaya karışma.
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return;
      const lines = [];
      for (let r = selNorm.r1; r <= selNorm.r2; r += 1) {
        const orig = renderRows[r]?.original;
        if (!orig) continue;
        const cells = [];
        for (let c = selNorm.c1; c <= selNorm.c2; c += 1) {
          const colId = visibleLeafCols[c]?.id;
          cells.push(colId ? (orig[colId] ?? '') : '');
        }
        lines.push(cells.join('\t'));
      }
      const tsv = lines.join('\n');
      if (tsv) { navigator.clipboard?.writeText(tsv); setCopied(true); setTimeout(() => setCopied(false), 1200); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rangeEnabled, selNorm, renderRows, visibleLeafCols]);

  // ── Özet/toplam satırı (filtreli tüm satırlar üzerinde) ──
  const totals = useMemo(() => {
    const out = {};
    columns.forEach((col) => {
      const agg = aggOf(col);
      if (agg === 'none' || !isNumericCol(col)) return;
      const vals = filteredOriginals.map((r) => Number(r[col.key]) || 0);
      if (!vals.length) { out[col.key] = 0; return; }
      out[col.key] = agg === 'avg' ? vals.reduce((a, b) => a + b, 0) / vals.length : vals.reduce((a, b) => a + b, 0);
    });
    return out;
  }, [columns, filteredOriginals]);
  const hasTotals = Object.keys(totals).length > 0;

  // ── Sıralama (çoklu: shift ile) ──
  const onSort = useCallback((key, e) => {
    const multi = e?.shiftKey;
    const cur = view.sorting;
    const idx = cur.findIndex((s) => s.id === key);
    let next;
    if (idx === -1) {
      next = multi ? [...cur, { id: key, desc: false }] : [{ id: key, desc: false }];
    } else if (!cur[idx].desc) {
      next = cur.map((s, i) => (i === idx ? { ...s, desc: true } : s));
      if (!multi) next = [{ id: key, desc: true }];
    } else {
      // asc → desc → kaldır
      next = multi ? cur.filter((s) => s.id !== key) : [];
    }
    patch({ sorting: next });
  }, [view.sorting, patch]);
  const sortInfo = (key) => {
    const idx = view.sorting.findIndex((s) => s.id === key);
    if (idx === -1) return null;
    return { desc: view.sorting[idx].desc, order: view.sorting.length > 1 ? idx + 1 : null };
  };

  // ── Kolon filtresi ──
  // Varsayılan mod DEĞER LİSTESİ: kullanıcı aradığı metni bilmek zorunda kalmasın, kolonda
  // fiilen bulunan değerlerden işaretlesin (Excel/DevExpress alışkanlığı). Sayısal kolonlarda
  // karşılaştırma (≥, ≤ …) ikinci sekme olarak KORUNUR — aralık sorgusu liste ile yapılamaz.
  const openColFilter = useCallback((key, e) => {
    e.stopPropagation();
    setOpenFilter({ key, rect: e.currentTarget.getBoundingClientRect() });
    setFilterSearch('');
    const existing = view.filters[key];
    if (Array.isArray(existing?.values)) setDraft({ mode: 'values', values: existing.values });
    else if (existing && typeof existing === 'object') setDraft({ mode: 'compare', op: existing.op || 'ge', val: existing.val ?? '' });
    // Eski düz-metin filtresi kayıtlı görünümden gelmiş olabilir; listeye taşıyamayız,
    // bu yüzden boş listeyle açılır (uygulanmış filtre başlıktaki huniden görünür).
    else setDraft({ mode: 'values', values: [] });
  }, [view.filters]);

  // Kolonun GERÇEK değer kümesi — huni açıldığında tüm satırlardan çıkarılır.
  // Etiket, tabloda görünen biçimlendirmeyi kullanır (col.fmt); ham değer anahtar kalır ki
  // filtre karşılaştırması biçimlendirmeden etkilenmesin.
  const distinctValues = useMemo(() => {
    const col = openFilter && colByKey[openFilter.key];
    if (!col) return [];
    const seen = new Map();
    for (const r of rows) {
      const raw = r[col.key];
      const k = String(raw ?? '');
      if (!seen.has(k)) seen.set(k, k === '' ? '(Boş)' : (col.fmt ? String(col.fmt(raw)) : k));
    }
    const coll = new Intl.Collator('tr', { numeric: true, sensitivity: 'base' });
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => (a.value === '' ? -1 : b.value === '' ? 1 : coll.compare(a.label, b.label)));
  }, [openFilter, colByKey, rows]);

  // Aramaya uyan değerler. Uzun listelerde (müşteri adı ~6000) hepsini DOM'a basmak
  // popover'ı kilitliyor → görünen kısım sınırlanır, kullanıcı aramayla daraltır.
  const VALUE_CAP = 400;
  const matchedValues = useMemo(() => {
    const q = filterSearch.trim().toLocaleLowerCase('tr');
    if (!q) return distinctValues;
    return distinctValues.filter((o) => o.label.toLocaleLowerCase('tr').includes(q));
  }, [distinctValues, filterSearch]);
  const shownValues = matchedValues.slice(0, VALUE_CAP);

  const toggleFilterValue = useCallback((v) => setDraft((d) => {
    const cur = d?.values || [];
    return { ...d, mode: 'values', values: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  }), []);

  const applyColFilter = useCallback(() => {
    const key = openFilter?.key; if (!key) return;
    const next = { ...view.filters };
    if (draft?.mode === 'compare') {
      const empty = draft.val === '' || draft.val == null;
      if (empty) delete next[key]; else next[key] = { op: draft.op || 'ge', val: draft.val };
    } else {
      const picked = draft?.values || [];
      // Hiçbiri ya da HEPSİ seçili = filtre yok; aksi hâlde gereksiz bir filtre rozeti kalırdı.
      if (picked.length === 0 || picked.length === distinctValues.length) delete next[key];
      else next[key] = { values: picked };
    }
    patch({ filters: next }); setOpenFilter(null);
  }, [openFilter, draft, distinctValues.length, view.filters, patch]);
  const clearColFilter = useCallback(() => {
    const key = openFilter?.key; if (!key) return;
    const next = { ...view.filters }; delete next[key];
    patch({ filters: next }); setOpenFilter(null);
  }, [openFilter, view.filters, patch]);

  // ── Kolon taşıma (başlık + panel sürüklemesi ortak) ──
  const moveColumn = useCallback((from, to) => {
    if (!from || from === to || colByKey[to]?.sticky) return;
    const order = [...view.order];
    const fromIdx = order.indexOf(from); const toIdx = order.indexOf(to);
    if (fromIdx < 0 || toIdx < 0) return;
    order.splice(fromIdx, 1); order.splice(toIdx, 0, from);
    patch({ order });
  }, [colByKey, view.order, patch]);
  const onDragStart = (key) => (e) => { if (colByKey[key]?.sticky) { e.preventDefault(); return; } dragKey.current = key; e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (key) => (e) => { if (!dragKey.current || dragKey.current === key || colByKey[key]?.sticky) return; e.preventDefault(); setDragOverKey(key); };
  const onDrop = (key) => (e) => { e.preventDefault(); const from = dragKey.current; dragKey.current = null; setDragOverKey(null); moveColumn(from, key); };
  const onPanelDragStart = (key) => (e) => { if (colByKey[key]?.sticky) { e.preventDefault(); return; } panelDragKey.current = key; e.dataTransfer.effectAllowed = 'move'; };
  const onPanelDragOver = (key) => (e) => { if (!panelDragKey.current || panelDragKey.current === key || colByKey[key]?.sticky) return; e.preventDefault(); setPanelDragOverKey(key); };
  const onPanelDrop = (key) => (e) => { e.preventDefault(); const from = panelDragKey.current; panelDragKey.current = null; setPanelDragOverKey(null); moveColumn(from, key); };

  const onResize = (key, startX, startWidth) => (e) => {
    e.preventDefault(); e.stopPropagation();
    const move = (ev) => patch({ widths: { ...view.widths, [key]: Math.max(64, startWidth + (ev.clientX - startX)) } });
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  };

  // ── Koşullu biçimlendirme (veri barı / ısı haritası) ──
  // Biçimli kolonlar için görünen satırlar üzerinden min/max.
  const formatStats = useMemo(() => {
    const stats = {};
    Object.keys(view.cellFormat).forEach((k) => {
      const col = colByKey[k];
      if (!col || !isNumericCol(col)) return;
      let min = Infinity; let max = -Infinity;
      filteredOriginals.forEach((r) => {
        const v = Number(r[k]);
        if (!Number.isNaN(v)) { if (v < min) min = v; if (v > max) max = v; }
      });
      stats[k] = { min: min === Infinity ? 0 : min, max: max === -Infinity ? 0 : max };
    });
    return stats;
  }, [view.cellFormat, filteredOriginals, colByKey]);

  // Bir hücre için koşullu biçim stili (background). none → null.
  const formatStyle = (key, raw) => {
    const kind = view.cellFormat[key];
    if (!kind) return null;
    const st = formatStats[key];
    if (!st) return null;
    const v = Number(raw); if (Number.isNaN(v)) return null;
    // Sabit kolonda (min===max) oran 1 kabul edilir → biçim yine GÖRÜNÜR olsun (aksi halde
    // "çalışmıyor" gibi görünüyordu). Aralık varsa normal ölçekleme.
    const frac = st.max === st.min ? 1 : Math.max(0, Math.min(1, (v - st.min) / (st.max - st.min)));
    if (kind === 'databar') {
      const pct = Math.round(frac * 100);
      const bar = 'color-mix(in srgb, var(--accent, #0073ea) 34%, transparent)';
      return { background: `linear-gradient(90deg, ${bar} 0 ${pct}%, transparent ${pct}% 100%)` };
    }
    // heatmap: açık → koyu accent (sabit kolonda orta ton). Yeşil-sarı-kırmızı yerine tek
    // accent tonu; düşük değer açık, yüksek değer koyu — belirgin olsun diye 16→64%.
    return { background: `color-mix(in srgb, var(--accent, #0073ea) ${Math.round(16 + frac * 48)}%, transparent)`, color: frac > 0.6 ? '#fff' : undefined };
  };

  const cycleFormat = (key) => {
    const cur = view.cellFormat[key];
    const next = cur === 'databar' ? 'heatmap' : cur === 'heatmap' ? undefined : 'databar';
    const cf = { ...view.cellFormat };
    if (next) cf[key] = next; else delete cf[key];
    patch({ cellFormat: cf });
    // Biçim AÇILINCA kolonu görünür alana kaydır — "açtım ama göremiyorum" olmasın.
    if (next) {
      requestAnimationFrame(() => {
        const el = scrollRef.current?.querySelector(`th[data-colkey="${key}"]`);
        el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
    }
  };

  const toggleHidden = (key) => patch({ hidden: view.hidden.includes(key) ? view.hidden.filter((k) => k !== key) : [...view.hidden, key] });
  const toggleGroup = (key) => { if (colByKey[key]?.sticky) return; patch({ grouping: grouping.includes(key) ? grouping.filter((k) => k !== key) : [...grouping, key] }); };
  const togglePinRight = (key) => { if (colByKey[key]?.sticky) return; patch({ pinnedRight: view.pinnedRight.includes(key) ? view.pinnedRight.filter((k) => k !== key) : [...view.pinnedRight, key] }); };

  // Sağa dondurulan kolonlar için sağ-offset (kümülatif genişlik).
  const rightOffsets = useMemo(() => {
    const visible = table.getVisibleLeafColumns();
    const pinned = visible.filter((c) => view.pinnedRight.includes(c.id));
    const map = {}; let acc = 0;
    for (let i = pinned.length - 1; i >= 0; i -= 1) { map[pinned[i].id] = acc; acc += pinned[i].getSize(); }
    return map;
  }, [table, view.pinnedRight, view.widths, view.order, view.hidden]); // eslint-disable-line react-hooks/exhaustive-deps

  const cellPinStyle = (col) => {
    if (col.sticky) return { position: 'sticky', left: 0, zIndex: 2 };
    if (view.pinnedRight.includes(col.key)) return { position: 'sticky', right: rightOffsets[col.key] || 0, zIndex: 2 };
    return null;
  };

  const headerGroups = table.getHeaderGroups();
  const leafHeaders = useMemo(
    () => headerGroups[headerGroups.length - 1]?.headers ?? [],
    [headerGroups],
  );
  const hiddenCount = view.hidden.length;

  // Gruplu başlık (ör. "Rut Dışı | Rut İçi" spanning başlık): görünür kolonlar arasındaki
  // BİTİŞİK aynı `group` etiketli koşuları birleştirir. Hiçbir kolonun `group`'u yoksa null
  // döner ve tek satır başlık render edilir (7 düz rapor bundan etkilenmez).
  const groupHeaderRuns = useMemo(() => {
    const cols = leafHeaders.map((h) => h.column.columnDef.meta);
    if (!cols.some((c) => c.group)) return null;
    const runs = [];
    cols.forEach((c) => {
      const label = c.group || '';
      const last = runs[runs.length - 1];
      if (last && last.label === label) last.span += 1;
      else runs.push({ label, span: 1, startKey: c.key });
    });
    return runs;
  }, [leafHeaders]);
  let leafSeq = safePage * view.pageSize; // düz modda satır numarası

  return (
    <div className={`rg-root${view.density === 'compact' ? ' rg-compact' : ''}`}>
      {/* Araç çubuğu */}
      <div className="rg-toolbar">
        <div className="rg-seg">
          {[['table', 'Tablo'], ['chart', 'Grafik'], ['pivot', 'Pivot']].map(([m, lbl]) => (
            <button key={m} type="button" className={`rg-seg-btn${mode === m ? ' on' : ''}`} onClick={() => setMode(m)}>{lbl}</button>
          ))}
        </div>
        {mode === 'table' && (
        <button type="button" className={`rg-tool-btn${panelOpen ? ' active' : ''}`} onClick={togglePanel} title="Kolon panelini aç/kapat">
          <ColumnsIcon /> Kolonlar
          {hiddenCount > 0 && <span className="rg-badge">{columns.length - hiddenCount}/{columns.length}</span>}
        </button>
        )}
        <div className="rg-search">
          <SearchIcon />
          <input
            placeholder="Tüm kolonlarda ara…"
            value={view.globalFilter}
            onChange={(e) => { patch({ globalFilter: e.target.value }); setPageIndex(0); }}
          />
          {view.globalFilter && <button type="button" className="rg-search-x" onClick={() => patch({ globalFilter: '' })}>✕</button>}
        </div>
        <button
          type="button"
          className="rg-tool-btn"
          onClick={() => patch({ density: view.density === 'compact' ? 'comfortable' : 'compact' })}
          title="Satır yoğunluğu"
        >
          <DensityIcon /> {view.density === 'compact' ? 'Rahat' : 'Sık'}
        </button>
        {isCustomized && (
          <button type="button" className="rg-tool-btn ghost" onClick={reset} title="Tüm özelleştirmeleri varsayılana döndür">
            <ResetIcon /> Görünümü Sıfırla
          </button>
        )}
        <div className="rg-viewmenu-wrap">
          <button type="button" className="rg-tool-btn" onClick={() => setViewMenuOpen((o) => !o)} title="Kayıtlı görünümler">
            <SaveIcon /> Görünümler{savedViews.length > 0 && <span className="rg-badge">{savedViews.length}</span>}
          </button>
          {viewMenuOpen && (
            <>
              <div className="rg-backdrop" onClick={() => setViewMenuOpen(false)} />
              <div className="rg-viewmenu" role="dialog" aria-label="Kayıtlı görünümler">
                <div className="rg-chooser-title">Kayıtlı Görünümler</div>
                {savedViews.length === 0 && <div className="rg-viewmenu-empty">Henüz kayıtlı görünüm yok. Kolon düzeni/filtre kurup "Kaydet…" ile saklayın.</div>}
                {savedViews.map((v) => (
                  <div key={v.id} className="rg-viewmenu-row">
                    <button type="button" className="rg-viewmenu-apply" onClick={() => { applyView(v.id); setViewMenuOpen(false); }}>{v.name}</button>
                    <button type="button" className="rg-viewmenu-del" title="Sil" onClick={() => deleteView(v.id)}>✕</button>
                  </div>
                ))}
                <button type="button" className="rg-viewmenu-save" onClick={() => { const n = window.prompt('Görünüm adı:'); if (n) { saveCurrentView(n); setViewMenuOpen(false); } }}>
                  + Bu görünümü kaydet…
                </button>
              </div>
            </>
          )}
        </div>
        {rangeEnabled && sel && <span className="rg-hint-chip" title="Ctrl/Cmd+C ile kopyala">aralık seçili · ⌘C</span>}
        <span className="rg-toolbar-spacer" />
        {copied && <span className="rg-copied">Kopyalandı ✓</span>}
        <span className="rg-count-chip">{filteredCount} satır</span>
      </div>

      {mode === 'chart' && <ReportChart rows={filteredOriginals} columns={columns} />}
      {mode === 'pivot' && (
        <ReportPivot
          rows={filteredOriginals}
          columns={columns}
          config={view.pivot}
          onConfigChange={(pivot) => patch({ pivot })}
        />
      )}

      {mode === 'table' && (
      <div className="rg-body">
        <div className="rg-main">
          <div className="rg-scroll" ref={scrollRef}>
            <table className="rg-table">
              <thead>
                {groupHeaderRuns && (
                  <tr className="rg-group-header-row">
                    {idColumn && <th className="rg-th rg-th-idx" rowSpan={2} />}
                    {groupHeaderRuns.map((run) => (
                      <th
                        key={run.startKey}
                        colSpan={run.span}
                        className={`rg-th rg-group-head${run.label ? '' : ' rg-group-blank'}`}
                      >
                        {run.label || ''}
                      </th>
                    ))}
                  </tr>
                )}
                <tr>
                  {idColumn && !groupHeaderRuns && <th className="rg-th rg-th-idx">#</th>}
                  {leafHeaders.map((header) => {
                    const col = header.column.columnDef.meta;
                    const active = view.filters[col.key] !== undefined;
                    const rightAlign = col.num || col.pct;
                    const isDragOver = dragOverKey === col.key;
                    const canFilter = filterTypeOf(col) !== false;
                    const si = sortInfo(col.key);
                    const pin = cellPinStyle(col);
                    return (
                      <th
                        key={col.key}
                        data-colkey={col.key}
                        className={`rg-th${col.sticky ? ' rg-th-sticky' : ''}${view.pinnedRight.includes(col.key) ? ' rg-th-pinned' : ''}${isDragOver ? ' rg-th-dragover' : ''}`}
                        style={{ width: header.getSize(), ...(pin || {}) }}
                        draggable={!col.sticky}
                        onDragStart={onDragStart(col.key)}
                        onDragOver={onDragOver(col.key)}
                        onDrop={onDrop(col.key)}
                        onDragEnd={() => { dragKey.current = null; setDragOverKey(null); }}
                      >
                        <div className="rg-th-inner" style={{ justifyContent: rightAlign ? 'flex-end' : 'space-between' }}>
                          {!col.sticky && <span className="rg-grip" title="Sürükleyerek taşı">⠿</span>}
                          <span className="rg-th-label" onClick={(e) => onSort(col.key, e)} title="Sırala (çoklu için Shift)">
                            {col.label}
                            {si && <span className="rg-sort-ind">{si.desc ? ' ▼' : ' ▲'}{si.order ? <sup>{si.order}</sup> : ''}</span>}
                          </span>
                          {canFilter && (
                            <button type="button" className={`rg-funnel${active ? ' active' : ''}`} title="Filtrele" onClick={(e) => openColFilter(col.key, e)}>
                              <FunnelIcon filled={active} />
                            </button>
                          )}
                        </div>
                        <span className="rg-resizer" onMouseDown={(e) => onResize(col.key, e.clientX, header.getSize())(e)} onClick={(e) => e.stopPropagation()} />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {renderRows.map((row, rIdx) => {
                  if (row.getIsGrouped()) {
                    return (
                      <tr key={row.id} className="rg-group-row">
                        {idColumn && <td className="rg-td rg-td-idx" />}
                        {row.getVisibleCells().map((cell) => {
                          const col = cell.column.columnDef.meta;
                          const pin = cellPinStyle(col);
                          if (cell.getIsGrouped()) {
                            return (
                              <td key={col.key} className={`rg-td rg-group-cell${col.sticky ? ' rg-td-sticky' : ''}`} style={{ paddingLeft: 12 + row.depth * 14, ...(pin || {}) }}>
                                <button type="button" className="rg-caret" onClick={row.getToggleExpandedHandler()}>{row.getIsExpanded() ? '▾' : '▸'}</button>
                                <strong>{cell.getValue() ?? '—'}</strong>
                                <span className="rg-group-count">{row.subRows.length}</span>
                              </td>
                            );
                          }
                          if (cell.getIsAggregated() && isNumericCol(col)) {
                            const v = cell.getValue();
                            return <td key={col.key} className={`rg-td rg-agg-cell${view.pinnedRight.includes(col.key) ? ' rg-td-pinned' : ''}`} style={{ textAlign: 'right', ...(pin || {}) }}>{col.fmt ? col.fmt(v) : v}</td>;
                          }
                          return <td key={col.key} className="rg-td" style={{ ...(pin || {}) }} />;
                        })}
                      </tr>
                    );
                  }
                  leafSeq += 1;
                  const seq = leafSeq;
                  return (
                    <tr key={row.id}>
                      {idColumn && <td className="rg-td rg-td-idx">{isGrouped ? '' : seq}</td>}
                      {row.getVisibleCells().map((cell, cIdx) => {
                        const col = cell.column.columnDef.meta;
                        const raw = cell.getValue();
                        const pin = cellPinStyle(col);
                        const pinCls = view.pinnedRight.includes(col.key) ? ' rg-td-pinned' : (col.sticky ? ' rg-td-sticky' : '');
                        // Aralık seçimi (Excel gibi): hücreye bas-sürükle, Ctrl/Cmd+C ile kopyala.
                        const selCls = rangeEnabled && inSel(rIdx, cIdx) ? ' rg-td-sel' : '';
                        const rangeProps = rangeEnabled ? {
                          onMouseDown: (e) => { if (e.button !== 0) return; selecting.current = true; setSel({ r1: rIdx, c1: cIdx, r2: rIdx, c2: cIdx }); e.preventDefault(); },
                          onMouseEnter: () => { if (selecting.current) setSel((s) => (s ? { ...s, r2: rIdx, c2: cIdx } : s)); },
                        } : {};
                        if (col.cell) {
                          return <td key={col.key} {...rangeProps} className={`rg-td${pinCls}${selCls}`} style={{ textAlign: col.num || col.pct ? 'right' : 'left', ...(pin || {}) }}>{col.cell(row.original)}</td>;
                        }
                        const tone = col.tone ? col.tone(raw) : null;
                        const fmtStyle = isNumericCol(col) ? formatStyle(col.key, raw) : null;
                        return (
                          <td
                            key={col.key}
                            {...rangeProps}
                            className={`rg-td${pinCls}${selCls}`}
                            style={{ textAlign: col.num ? 'right' : 'left', fontWeight: col.bold ? 800 : (col.sticky ? 700 : 500), color: tone || undefined, ...(fmtStyle || {}), ...(pin || {}) }}
                          >
                            {col.fmt ? col.fmt(raw) : (raw ?? '—')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              {hasTotals && (
                <tfoot>
                  <tr className="rg-totals-row">
                    {idColumn && <td className="rg-td rg-td-idx" />}
                    {leafHeaders.map((header, i) => {
                      const col = header.column.columnDef.meta;
                      const pin = cellPinStyle(col);
                      const agg = aggOf(col);
                      const has = isNumericCol(col) && agg !== 'none';
                      return (
                        <td
                          key={col.key}
                          className={`rg-td rg-totals-cell${col.sticky ? ' rg-td-sticky' : ''}${view.pinnedRight.includes(col.key) ? ' rg-td-pinned' : ''}`}
                          style={{ textAlign: has ? 'right' : 'left', ...(pin || {}) }}
                        >
                          {col.sticky || i === 0 ? <span className="rg-totals-label">TOPLAM</span> : (has ? (col.fmt ? col.fmt(totals[col.key]) : totals[col.key]) : '')}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {!isGrouped && (
            <div className="rg-footer">
              <span className="rg-footer-info">
                Sayfa {safePage + 1} / {pageCount} · {filteredCount} satır
                {filteredCount !== rows.length ? ` (${rows.length} içinden)` : ''}
              </span>
              <div className="rg-pager">
                <button type="button" className="rg-page-btn" disabled={safePage === 0} onClick={() => setPageIndex(0)}>«</button>
                <button type="button" className="rg-page-btn" disabled={safePage === 0} onClick={() => setPageIndex((p) => Math.max(0, p - 1))}>‹</button>
                {pageWindow(safePage + 1, pageCount).map((p, i) => (
                  p === '…'
                    ? <span key={`e${i}`} className="rg-page-ell">…</span>
                    : <button key={p} type="button" className={`rg-page-btn${p === safePage + 1 ? ' current' : ''}`} onClick={() => setPageIndex(p - 1)}>{p}</button>
                ))}
                <button type="button" className="rg-page-btn" disabled={safePage >= pageCount - 1} onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}>›</button>
                <button type="button" className="rg-page-btn" disabled={safePage >= pageCount - 1} onClick={() => setPageIndex(pageCount - 1)}>»</button>
                <select className="rg-page-size" value={view.pageSize} onChange={(e) => { patch({ pageSize: Number(e.target.value) }); setPageIndex(0); }}>
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / sayfa</option>)}
                </select>
              </div>
            </div>
          )}
          {isGrouped && <div className="rg-footer"><span className="rg-footer-info">{grouping.length} kolona göre gruplandı · {filteredCount} satır</span></div>}
        </div>

        {panelOpen && (
          <aside className="rg-panel" aria-label="Kolon paneli">
            <div className="rg-panel-head">
              <span className="rg-panel-title"><ColumnsIcon /> Kolonlar</span>
              <button type="button" className="rg-panel-x" onClick={togglePanel} title="Paneli kapat">✕</button>
            </div>

            {/* Gruplama alanı */}
            <div className="rg-group-zone">
              <div className="rg-group-zone-title">Gruplama</div>
              {grouping.length === 0
                ? <div className="rg-group-empty">Aşağıdan bir metin kolonunun ⊞ simgesine basarak grupla</div>
                : (
                  <div className="rg-group-chips">
                    {grouping.map((k) => (
                      <span key={k} className="rg-group-chip">
                        {colByKey[k]?.label}
                        <button type="button" onClick={() => toggleGroup(k)} title="Gruplamadan çıkar">✕</button>
                      </span>
                    ))}
                  </div>
                )}
            </div>

            <div className="rg-panel-tools">
              <button type="button" className="rg-panel-link" onClick={() => patch({ hidden: [] })}>Tümünü göster</button>
              <span className="rg-panel-dot">·</span>
              <button type="button" className="rg-panel-link" onClick={() => patch({ hidden: columns.filter((c) => !c.sticky).map((c) => c.key) })}>Tümünü gizle</button>
            </div>

            <div className="rg-panel-list">
              {view.order.map((key) => {
                const col = colByKey[key]; if (!col) return null;
                const visible = !view.hidden.includes(key);
                const isOver = panelDragOverKey === key;
                const canGroup = !isNumericCol(col) && !col.sticky;
                const grouped = grouping.includes(key);
                const pinned = view.pinnedRight.includes(key);
                return (
                  <div
                    key={key}
                    className={`rg-panel-row${col.sticky ? ' sticky' : ''}${isOver ? ' dragover' : ''}`}
                    draggable={!col.sticky}
                    onDragStart={onPanelDragStart(key)}
                    onDragOver={onPanelDragOver(key)}
                    onDrop={onPanelDrop(key)}
                    onDragEnd={() => { panelDragKey.current = null; setPanelDragOverKey(null); }}
                  >
                    <span className="rg-panel-grip">⠿</span>
                    <label className="rg-panel-check">
                      <input type="checkbox" checked={visible} disabled={col.sticky} onChange={() => toggleHidden(key)} />
                      <span>{col.label}</span>
                    </label>
                    {col.sticky
                      ? <span className="rg-panel-tag">sabit</span>
                      : (
                        <span className="rg-panel-actions">
                          {canGroup && (
                            <button type="button" className={`rg-panel-ico${grouped ? ' on' : ''}`} title={grouped ? 'Gruplamadan çıkar' : 'Bu kolona göre grupla'} onClick={() => toggleGroup(key)}>⊞</button>
                          )}
                          {isNumericCol(col) && (
                            <button
                              type="button"
                              className={`rg-panel-ico rg-fmt-ico${view.cellFormat[key] ? ' on' : ''}`}
                              title={view.cellFormat[key] === 'databar' ? 'Veri barı açık — tıkla: ısı haritası' : view.cellFormat[key] === 'heatmap' ? 'Isı haritası açık — tıkla: kapat' : 'Koşullu biçim ekle (veri barı → ısı → kapalı)'}
                              onClick={() => cycleFormat(key)}
                            >
                              {view.cellFormat[key] === 'heatmap' ? <HeatIcon /> : view.cellFormat[key] === 'databar' ? <BarIcon /> : <FmtOffIcon />}
                            </button>
                          )}
                          <button type="button" className={`rg-panel-ico${pinned ? ' on' : ''}`} title={pinned ? 'Dondurmayı kaldır' : 'Sağa dondur'} onClick={() => togglePinRight(key)}>📌</button>
                        </span>
                      )}
                  </div>
                );
              })}
            </div>
            <div className="rg-panel-hint">⊞ grupla · ▬ veri barı/ısı · 📌 sağa dondur · satırı sürükle = sırala</div>
          </aside>
        )}
      </div>
      )}

      {openFilter && (() => {
        const col = colByKey[openFilter.key];
        const isNum = filterTypeOf(col) === 'number';
        const isCompare = draft?.mode === 'compare';
        const picked = draft?.values || [];
        const allShownPicked = shownValues.length > 0 && shownValues.every((o) => picked.includes(o.value));
        const left = Math.max(8, Math.min(openFilter.rect.left, window.innerWidth - 320));
        const top = Math.min(openFilter.rect.bottom + 6, window.innerHeight - 380);
        return (
          <>
            <div className="rg-backdrop" onClick={() => setOpenFilter(null)} />
            <div className="rg-popover rg-popover-wide" style={{ left, top }} onClick={(e) => e.stopPropagation()}>
              <div className="rg-pop-title">{col.label} — Filtre</div>

              {/* Sayısal kolonda iki mod: değer listesi + karşılaştırma (aralık listeyle kurulamaz). */}
              {isNum && (
                <div className="rg-pop-tabs">
                  <button type="button" className={`rg-pop-tab${!isCompare ? ' on' : ''}`} onClick={() => setDraft((d) => ({ ...d, mode: 'values', values: d?.values || [] }))}>Liste</button>
                  <button type="button" className={`rg-pop-tab${isCompare ? ' on' : ''}`} onClick={() => setDraft((d) => ({ ...d, mode: 'compare', op: d?.op || 'ge', val: d?.val ?? '' }))}>Karşılaştır</button>
                </div>
              )}

              {isCompare ? (
                <div className="rg-pop-num">
                  <select value={draft?.op || 'ge'} onChange={(e) => setDraft((d) => ({ ...(d || {}), op: e.target.value }))}>
                    {NUM_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <input autoFocus type="number" placeholder="Değer…" value={draft?.val ?? ''} onChange={(e) => setDraft((d) => ({ ...(d || {}), mode: 'compare', op: (d && d.op) || 'ge', val: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') applyColFilter(); }} />
                </div>
              ) : (
                <>
                  <input
                    autoFocus
                    className="rg-pop-text"
                    placeholder="Listede ara…"
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                  />
                  <label className="rg-pop-opt rg-pop-all">
                    <input
                      type="checkbox"
                      checked={allShownPicked}
                      onChange={() => setDraft((d) => {
                        const cur = d?.values || [];
                        const shown = shownValues.map((o) => o.value);
                        return {
                          ...d,
                          mode: 'values',
                          values: allShownPicked ? cur.filter((v) => !shown.includes(v)) : [...new Set([...cur, ...shown])],
                        };
                      })}
                    />
                    <span>(Tümünü seç{filterSearch ? ' — aramadaki' : ''})</span>
                  </label>
                  <div className="rg-pop-list">
                    {shownValues.length === 0 && <div className="rg-pop-empty">Sonuç yok</div>}
                    {shownValues.map((o) => (
                      <label key={o.value} className="rg-pop-opt">
                        <input type="checkbox" checked={picked.includes(o.value)} onChange={() => toggleFilterValue(o.value)} />
                        <span title={o.label}>{o.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="rg-pop-note">
                    {matchedValues.length > shownValues.length
                      ? `${matchedValues.length} değerden ilk ${shownValues.length}'i — daraltmak için arayın`
                      : `${picked.length} / ${distinctValues.length} seçili`}
                  </div>
                </>
              )}

              <div className="rg-pop-btns">
                <button type="button" className="rg-pop-btn" onClick={clearColFilter}>Temizle</button>
                <button type="button" className="rg-pop-btn primary" onClick={applyColFilter}>Uygula</button>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}

function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const lo = Math.max(2, current - 1);
  const hi = Math.min(total - 1, current + 1);
  if (lo > 2) out.push('…');
  for (let p = lo; p <= hi; p += 1) out.push(p);
  if (hi < total - 1) out.push('…');
  out.push(total);
  return out;
}

const ColumnsIcon = () => (<svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="18" /><rect x="14" y="3" width="7" height="18" /></svg>);
const ResetIcon = () => (<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>);
const SearchIcon = () => (<svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>);
const SaveIcon = () => (<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></svg>);
const DensityIcon = () => (<svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>);
const FunnelIcon = ({ filled }) => (<svg width={13} height={13} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>);
// Koşullu biçim ikonları: kapalı (soluk bar taslağı) · veri barı (dolu bar) · ısı (gradyan kutu)
const FmtOffIcon = () => (<svg width={13} height={13} viewBox="0 0 16 16"><rect x="1" y="4" width="14" height="2.4" rx="1" fill="none" stroke="currentColor" strokeWidth="1.2" opacity=".6" /><rect x="1" y="9.5" width="9" height="2.4" rx="1" fill="none" stroke="currentColor" strokeWidth="1.2" opacity=".6" /></svg>);
const BarIcon = () => (<svg width={13} height={13} viewBox="0 0 16 16"><rect x="1" y="3.5" width="13" height="3" rx="1.5" fill="currentColor" /><rect x="1" y="9" width="8" height="3" rx="1.5" fill="currentColor" /></svg>);
const HeatIcon = () => (<svg width={13} height={13} viewBox="0 0 16 16"><defs><linearGradient id="rgheat" x1="0" x2="1"><stop offset="0" stopColor="currentColor" stopOpacity=".2" /><stop offset="1" stopColor="currentColor" stopOpacity="1" /></linearGradient></defs><rect x="1" y="2.5" width="14" height="11" rx="2" fill="url(#rgheat)" /></svg>);
